import type { StorageItem, StorageResourceRef } from '../../../../types/azureStorage';
import { historicalBlobSasProblem } from '../../../../core/engines/azureStorageSasParameters';
import { hnsSasPathProblem } from '../../../../core/engines/azureStorageHnsSas';
import { hnsRefOfItem } from '../../../../core/engines/azureStorageHnsPaths';
import { storageItemRef } from '../utils/storagePaths';

export const SAS_FOLDER_REASON = 'A SAS covers the account, a container, a blob, a table or a queue; a virtual folder has no SAS of its own.';
/** S7: Azure Files signs a share (`sr=s`) or a file (`sr=f`); there is no folder SAS. */
export const SAS_SHARE_FOLDER_REASON = 'A SAS covers a whole file share or one file; Azure Files has no SAS for a folder.';
export const SAS_UNSUPPORTED_TARGET_REASON = 'A SAS cannot be generated for this item.';

export type SasTarget = Extract<StorageResourceRef, {
  kind: 'account' | 'container' | 'blob' | 'table' | 'queue'
    // S7
    | 'share' | 'file'
    // S8: a Data Lake directory (`sr=d`), the file-system root (container SAS) or a file (blob SAS)
    | 'hnsPath';
}>;

export function isSasTarget(resource: StorageResourceRef): resource is SasTarget {
  switch (resource.kind) {
    case 'account': case 'container': case 'blob': case 'table': case 'queue': case 'share': case 'file': case 'hnsPath': return true;
    case 'service': case 'blobPrefix': case 'entity': case 'message': case 'directory': return false;
    default: return (resource as { kind: never }).kind;
  }
}

/** Why "Get SAS…" is unavailable for this target (review C F1): every entry point disables with it, the dialog refuses. */
export function sasTargetProblem(resource: StorageResourceRef): string | undefined {
  if (resource.kind === 'blobPrefix') return SAS_FOLDER_REASON;
  if (resource.kind === 'directory') return SAS_SHARE_FOLDER_REASON;
  if (!isSasTarget(resource)) return SAS_UNSUPPORTED_TARGET_REASON;
  if (resource.kind === 'hnsPath') return hnsSasPathProblem(resource)?.message; // S8
  // A share / file snapshot: "A SAS for a share snapshot is not available; generate it for the live share."
  return historicalBlobSasProblem(resource)?.message;
}

/**
 * S8 Task 8.7: the SAS target of a listed row. On an account whose hierarchical namespace is confirmed a folder IS a
 * directory, so it gets a directory SAS; elsewhere a folder stays the virtual `blobPrefix` that `SAS_FOLDER_REASON`
 * refuses. A file keeps its blob ref (it signs as its blob either way).
 */
export function sasTargetForRow(row: { item: StorageItem }, options: { hns: 'yes' | 'no' | 'unknown' }): StorageResourceRef | null {
  if (options.hns === 'yes' && row.item.kind === 'folder') return hnsRefOfItem(row.item) ?? null;
  return storageItemRef(row.item);
}

/**
 * The SAS dialog's title — one for every target (UI/UX fix round A, U19: a Data Lake directory's dialog used to be titled
 * "Directory SAS"); the target summary names what is signed (a directory, the file-system root as a container — D7).
 */
export function sasDialogTitle(_resource: StorageResourceRef): string {
  return 'Generate SAS';
}
